import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Loader2, RefreshCw } from 'lucide-react'
import { ReindexDrawer } from './ReindexDrawer'
import type { AiReindexJob } from '@/lib/api/features/ai'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { getReindexJobs } from '@/lib/api/features/ai'

interface Props {
  workspaceSlug: string
}

/**
 * Card 3 — Reindex trigger + recent-jobs panel.
 *
 * The "Reindex workspace embeddings" button opens a drawer that walks the
 * admin through the lower-bound estimate (POST /ai/index?dryRun=true) and
 * the cost-confirm gate before actually enqueueing. The jobs table below
 * shows the last 10 reindex attempts joined with Hangfire's live state;
 * pending rows whose enqueue failed surface as `Failed-to-enqueue` rather
 * than disappearing.
 *
 * The jobs query polls every 5 seconds while at least one row is in flight
 * (Enqueued/Processing/Awaiting) so the panel reflects progress without a
 * page refresh; otherwise it relies on react-query's default staleness.
 */
export function ReindexCard({ workspaceSlug }: Props) {
  const { t } = useTranslation('settings')
  const [drawerOpen, setDrawerOpen] = useState(false)

  const jobsQuery = useQuery({
    queryKey: ['ai-reindex-jobs', workspaceSlug],
    queryFn: () => getReindexJobs(workspaceSlug, 10),
    refetchInterval: (query) => {
      // Cheap heuristic: poll while anything's in flight, otherwise stop.
      // Hangfire's terminal state names: Succeeded, Failed, Deleted (manual
      // purge), Failed-to-enqueue (our own marker). Everything else is
      // either active or transient and worth a refresh.
      const jobs = query.state.data?.jobs ?? []
      const inFlight = jobs.some((j) =>
        ['Enqueued', 'Processing', 'Awaiting', 'Scheduled', 'pending'].includes(j.state),
      )
      return inFlight ? 5000 : false
    },
  })

  const jobs = jobsQuery.data?.jobs ?? []

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <RefreshCw className="size-4 text-muted-foreground" />
              <CardTitle>{t('aiSettings.reindex.title')}</CardTitle>
            </div>
            <CardDescription>{t('aiSettings.reindex.description')}</CardDescription>
          </div>
          <Button onClick={() => setDrawerOpen(true)}>
            {t('aiSettings.reindex.trigger')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          {t('aiSettings.reindex.runbookHint')}
        </p>

        {jobsQuery.isLoading ? (
          <div className="flex h-24 items-center justify-center text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('aiSettings.reindex.emptyJobs')}
          </p>
        ) : (
          <ReindexJobsTable jobs={jobs} />
        )}
      </CardContent>

      <ReindexDrawer
        workspaceSlug={workspaceSlug}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onConfirmed={() => jobsQuery.refetch()}
      />
    </Card>
  )
}

function ReindexJobsTable({ jobs }: { jobs: Array<AiReindexJob> }) {
  const { t } = useTranslation('settings')
  const { smartTime } = useDateTimeFormatters()

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('aiSettings.reindex.state')}</TableHead>
            <TableHead>{t('aiSettings.reindex.scope')}</TableHead>
            <TableHead>{t('aiSettings.reindex.requested')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job) => (
            <TableRow key={job.reindexJobId}>
              <TableCell>
                <StateBadge state={job.state} />
              </TableCell>
              <TableCell className="text-sm">
                {job.sourceType ?? t('aiSettings.reindex.scopeAll')}
                {job.force && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    · {t('aiSettings.reindex.force')}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {smartTime(new Date(job.requestedOn))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function StateBadge({ state }: { state: string }) {
  // Map Hangfire + our custom states into three visual buckets so the table
  // is glanceable. Anything we don't recognise falls through to the default
  // grey badge with the raw label.
  const variant: 'default' | 'secondary' | 'destructive' = (() => {
    switch (state) {
      case 'Succeeded':
        return 'default'
      case 'Failed':
      case 'Failed-to-enqueue':
        return 'destructive'
      default:
        return 'secondary'
    }
  })()
  return <Badge variant={variant}>{state}</Badge>
}
